import React, {Fragment, useState} from 'react'
import {
	useClearRefinements,
	useCurrentRefinements,
	useNumericMenu,
	useRefinementList
} from 'react-instantsearch-hooks'

import {Icon} from 'assets/icon'
import {FilterTag} from 'layout/search/filtertag'
import AnimateHeight from 'react-animate-height'
import {CurrentRefinementsProps} from 'react-instantsearch-hooks-web/dist/es/ui/CurrentRefinements'
import {DPJobsBlock} from 'types/DPTypes'
import {useTranslation} from 'util/i18'
import {fromModule} from 'util/styler/Styler'
import css from './jobsfilters.module.scss'

const styles = fromModule(css)

type CurrentRefinementsItem = CurrentRefinementsProps['items'][0] & {
	refine: (r: unknown) => void
}

type NumericMenuItem = {
	value: string
	label: string
	isRefined: boolean
}

const careerKeys = [
	'type',
	'job_title',
	'categories',
	'region',
	'date_posted',
	'employment_type',
	'employer'
]
const academicKeys = [
	'employment_type',
	'categories',
	'region',
	'date_posted',
	'csr',
	'promotor',
	'competence'
]
export const algoliaFilterMap = {
	type: 'types',
	job_title: 'titles',
	categories: 'categories',
	region: 'regions',
	employment_type: 'employmenttypes',
	employer: 'employers',
	csr: 'csr',
	promotor: 'promotors',
	competence: 'competences'
}

const settingsFilter = 'currentrefinementsettings'

export const Jobsfilters: React.FC<{
	types: DPJobsBlock['types']
	tab: 'academic' | 'career'
	dateMenu: {label: string; start?: number}[]
}> = ({types, tab, dateMenu}) => {
	const [selectedFilter, setSelectedFilter] = useState(null)
	const [open, setOpen] = useState(false)
	const {items: refinements} = useCurrentRefinements()
	const {refine: clearAll} = useClearRefinements()
	const filterKeys = tab === 'academic' ? academicKeys : careerKeys
	const t = useTranslation()
	const {items: dateItems, refine: refineDate} = useNumericMenu({
		attribute: 'date_timestamp',
		items: dateMenu
	})

	return (
		<div className={styles.jobsfilters()}>
			<div className={styles.jobsfilters.items()}>
				<JobsfiltersSettings
					onClick={() => {
						setSelectedFilter(settingsFilter)
						setOpen((prevOpen) => !prevOpen || selectedFilter != settingsFilter)
					}}
					refinements={refinements}
					active={open && selectedFilter === settingsFilter}
				/>
				{filterKeys.map((filterKey) => {
					const selected = filterKey === selectedFilter && open
					return (
						<div
							key={filterKey}
							onClick={() => {
								setSelectedFilter(filterKey)
								setOpen((prevOpen) => !prevOpen || selectedFilter !== filterKey)
							}}
							className={styles.jobsfilters.item.is({
								active: !!refinements?.find((i) =>
									filterKey === 'date_posted'
										? i.attribute === 'date_timestamp'
										: i.attribute === algoliaFilterMap[filterKey]
								),
								selected
							})()}
						>
							{t.jobs.filters[filterKey]}
						</div>
					)
				})}
			</div>
			<div className={styles.jobsfilters.tags()}>
				<AnimateHeight height={open ? 'auto' : 0} animateOpacity={true}>
					<div
						key={'allRefinements' + tab}
						className={styles.jobsfilters.tags.is({
							hidden: selectedFilter !== settingsFilter
						})()}
					>
						<JobsfiltersRefinements
							refinements={refinements}
							types={types}
							clearAll={() => {
								clearAll()
								setOpen(false)
							}}
							dateMenu={dateMenu}
						/>
					</div>
					{filterKeys.map((filterKey) => {
						const selected = filterKey === selectedFilter
						if (filterKey === 'date_posted') {
							return (
								<div
									key={filterKey}
									className={styles.jobsfilters.tags.is({
										hidden: !selected
									})()}
								>
									<JobFiltersDate items={dateItems} refine={refineDate} />
								</div>
							)
						}
						return (
							<div
								key={filterKey}
								className={styles.jobsfilters.tags.is({
									hidden: !selected
								})()}
							>
								<JobFiltersTaxonomy types={types} filterKey={filterKey} />
							</div>
						)
					})}
				</AnimateHeight>
			</div>
			<hr className={styles.jobsfilters.line()} />
		</div>
	)
}

const JobFiltersTaxonomy: React.FC<{
	types: DPJobsBlock['types']
	filterKey: string
}> = ({types, filterKey}) => {
	const {items, refine} = useRefinementList({
		attribute: algoliaFilterMap[filterKey],
		limit: Math.max(types[filterKey].length, 10)
	})
	const {refine: clear} = useClearRefinements({
		includedAttributes: [algoliaFilterMap[filterKey]]
	})
	const t = useTranslation()

	const validTags = types[filterKey].filter(
		(f) => !!items.find((i) => i.value === f.tid)
	)
	const hasRefinements = !!items.find((i) => i.isRefined)

	return (
		<div>
			<FilterTag
				key={filterKey + '_all'}
				inactive={hasRefinements}
				onClick={() => clear()}
			>
				{t.jobs.all}
			</FilterTag>
			{validTags.map((tag, id) => {
				const algoliaFilter = items.find((i) => i.value === tag.tid)
				return (
					<FilterTag
						key={tag.tid + id}
						inactive={!algoliaFilter.isRefined}
						onClick={() => refine(algoliaFilter.value)}
						passive={
							tag.parent && items.find((i) => i.value === tag.parent)?.isRefined
						}
					>
						{tag.name}
					</FilterTag>
				)
			})}
		</div>
	)
}

const JobFiltersDate: React.FC<{
	items: NumericMenuItem[]
	refine: (v: string) => void
}> = ({items, refine}) => {
	const t = useTranslation()

	return (
		<div>
			{items.map((tag, id) => (
				<FilterTag
					key={id}
					inactive={!tag.isRefined}
					onClick={() => {
						if (tag.isRefined) {
							refine(items[0].value)
							return
						}
						refine(tag.value)
					}}
				>
					{tag.label === 'all' ? t.jobs.all : t.jobs.periods[tag.label]}
				</FilterTag>
			))}
		</div>
	)
}

const JobsfiltersSettings: React.FC<{
	onClick: () => void
	refinements: CurrentRefinementsItem[]
	active: boolean
}> = ({onClick, refinements, active}) => {
	return (
		<div
			className={styles.settings.is({
				active,
				filled: refinements.length > 0
			})()}
			onClick={refinements.length ? onClick : () => {}}
		>
			<Icon icon="Settings" />
			<div className={styles.settings.notice()}>
				<div className={styles.settings.notice.number()}>
					{refinements.reduce((a, c) => a + c.refinements.length, 0)}
				</div>
			</div>
		</div>
	)
}

const JobsfiltersRefinements: React.FC<{
	refinements: CurrentRefinementsItem[]
	types: DPJobsBlock['types']
	clearAll: () => void
	dateMenu: {label: string; start?: number}[]
}> = ({refinements, types, clearAll, dateMenu}) => {
	const t = useTranslation()
	return (
		<div>
			<FilterTag
				key={settingsFilter + '_all'}
				onClick={clearAll}
				inactive={true}
			>
				{t.jobs.clear_refinements}
			</FilterTag>
			{refinements.map((refinement, rId) => {
				const typesKey = Object.keys(algoliaFilterMap).find(
					(afkey) => algoliaFilterMap[afkey] === refinement.attribute
				)
				const getLabel = (refItem) => {
					if (refinement.attribute === 'date_timestamp') {
						const dateValue = dateMenu.find(
							(i) => i.start && i.start === refItem.value
						)
						if (!dateValue) return ''
						return t.jobs.periods[dateValue.label]
					}
					return types[typesKey].find((t) => t.tid === refItem.value)?.name
				}
				return (
					<Fragment key={rId}>
						{refinement.refinements.map((refItem, id) => (
							<FilterTag
								key={id}
								onClick={() => {
									if (
										refinements.length === 1 &&
										refinement.refinements.length === 1
									) {
										clearAll()
										return
									}
									refinement.refine(refItem)
								}}
							>
								{getLabel(refItem)}
							</FilterTag>
						))}
					</Fragment>
				)
			})}
		</div>
	)
}
